import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { recruiterAPI, jobsAPI, messagesAPI, institutionsAPI } from '../lib/api';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Briefcase, Search, Users, BarChart3, MessageCircle, Filter,
  Plus, Send, Loader2, ChevronRight, GraduationCap, MapPin,
  Building2, Star, Clock, ArrowRight, Sparkles, X, Eye,
  TrendingUp, Target, UserPlus, FileText
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow, format } from 'date-fns';
import usePageTitle from '../hooks/usePageTitle';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, LineChart, Line, Cell, FunnelChart, Funnel, LabelList
} from 'recharts';

const PIPELINE_STAGES = [
  { key: 'new', label: 'New', color: 'bg-blue-500', headerBg: 'bg-blue-50 border-blue-200', textColor: 'text-blue-700' },
  { key: 'reviewed', label: 'Reviewed', color: 'bg-indigo-500', headerBg: 'bg-indigo-50 border-indigo-200', textColor: 'text-indigo-700' },
  { key: 'contacted', label: 'Contacted', color: 'bg-purple-500', headerBg: 'bg-purple-50 border-purple-200', textColor: 'text-purple-700' },
  { key: 'interviewed', label: 'Interviewed', color: 'bg-amber-500', headerBg: 'bg-amber-50 border-amber-200', textColor: 'text-amber-700' },
  { key: 'offered', label: 'Offered', color: 'bg-emerald-500', headerBg: 'bg-emerald-50 border-emerald-200', textColor: 'text-emerald-700' },
  { key: 'hired', label: 'Hired', color: 'bg-green-600', headerBg: 'bg-green-50 border-green-200', textColor: 'text-green-700' },
  { key: 'rejected', label: 'Rejected', color: 'bg-red-500', headerBg: 'bg-red-50 border-red-200', textColor: 'text-red-700' },
];

const WORK_MODES = [
  { value: 'remote', label: 'Remote' },
  { value: 'onsite', label: 'On-Site' },
  { value: 'hybrid', label: 'Hybrid' },
];

const EXPERIENCE_LEVELS = [
  { value: 'entry', label: 'Entry Level' },
  { value: 'mid', label: 'Mid Level' },
  { value: 'senior', label: 'Senior' },
  { value: 'executive', label: 'Executive' },
];

const JOB_CATEGORIES = [
  'Engineering', 'Marketing', 'Finance', 'Healthcare', 'Education',
  'Design', 'Sales', 'Operations', 'Legal', 'HR', 'Data Science',
  'Product', 'Consulting', 'Research',
];

const GRADUATION_YEARS = Array.from({ length: 10 }, (_, i) => (new Date().getFullYear() - i).toString());

const RecruiterPanel = () => {
  usePageTitle('Recruiter');
  const { user } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);

  // Dashboard state
  const [dashboardData, setDashboardData] = useState(null);

  // Talent search state
  const [talentSearch, setTalentSearch] = useState('');
  const [talentInstitution, setTalentInstitution] = useState('all');
  const [talentMajor, setTalentMajor] = useState('');
  const [talentGradYear, setTalentGradYear] = useState('all');
  const [talentSkills, setTalentSkills] = useState('');
  const [talentLocation, setTalentLocation] = useState('');
  const [talentResults, setTalentResults] = useState([]);
  const [talentLoading, setTalentLoading] = useState(false);
  const [institutions, setInstitutions] = useState([]);

  // AI Match dialog
  const [aiMatchDialog, setAiMatchDialog] = useState(null);
  const [aiJobDescription, setAiJobDescription] = useState('');
  const [aiMatchResult, setAiMatchResult] = useState(null);
  const [aiMatchLoading, setAiMatchLoading] = useState(false);

  // Message dialog
  const [messageDialog, setMessageDialog] = useState(null);
  const [messageContent, setMessageContent] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);

  // Job postings state
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [showCreateJob, setShowCreateJob] = useState(false);
  const [creatingJob, setCreatingJob] = useState(false);
  const [newJob, setNewJob] = useState({
    title: '', company: '', description: '', location: '',
    work_mode: 'remote', experience_level: 'mid',
    salary_min: '', salary_max: '', category: '',
  });

  // Smart paste: AI-parse a raw job posting into form fields
  const [jobPasteText, setJobPasteText] = useState('');
  const [parsingJob, setParsingJob] = useState(false);

  // Pipeline state
  const [pipeline, setPipeline] = useState([]);
  const [pipelineLoading, setPipelineLoading] = useState(false);
  const [pipelineDetail, setPipelineDetail] = useState(null);
  const [pipelineStageUpdate, setPipelineStageUpdate] = useState('');
  const [pipelineNotes, setPipelineNotes] = useState('');
  const [updatingPipeline, setUpdatingPipeline] = useState(false);

  // Pipeline add dialog
  const [addToPipelineDialog, setAddToPipelineDialog] = useState(null);
  const [addPipelineJobId, setAddPipelineJobId] = useState('');
  const [addPipelineNotes, setAddPipelineNotes] = useState('');

  // Analytics state
  const [analyticsData, setAnalyticsData] = useState(null);

  useEffect(() => {
    if (!user?.roles?.includes('recruiter') && !user?.roles?.includes('admin') && user?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    fetchDashboard();
    fetchInstitutions();
  }, [user, navigate]);

  const fetchInstitutions = async () => {
    try {
      const res = await institutionsAPI.list();
      setInstitutions(res.data?.institutions || res.data || []);
    } catch (e) {
      // Institutions list is optional for filtering
    }
  };

  const fetchDashboard = async () => {
    try {
      const res = await recruiterAPI.getDashboard();
      setDashboardData(res.data);
    } catch (e) {
      toast.error('Failed to load recruiter dashboard');
    } finally {
      setLoading(false);
    }
  };

  const searchTalent = async () => {
    setTalentLoading(true);
    try {
      const params = {};
      if (talentSearch) params.q = talentSearch;
      if (talentInstitution && talentInstitution !== 'all') params.institution = talentInstitution;
      if (talentMajor) params.major = talentMajor;
      if (talentGradYear && talentGradYear !== 'all') params.graduation_year = talentGradYear;
      if (talentSkills) params.skills = talentSkills;
      if (talentLocation) params.location = talentLocation;
      const res = await recruiterAPI.searchTalent(params);
      setTalentResults(res.data?.candidates || res.data || []);
    } catch (e) {
      toast.error('Failed to search talent');
    } finally {
      setTalentLoading(false);
    }
  };

  const handleAiMatch = async () => {
    if (!aiMatchDialog || !aiJobDescription.trim()) return;
    setAiMatchLoading(true);
    setAiMatchResult(null);
    try {
      const res = await recruiterAPI.aiMatch(aiMatchDialog.user_id || aiMatchDialog._id, aiJobDescription);
      setAiMatchResult(res.data);
    } catch (e) {
      toast.error('AI matching failed');
    } finally {
      setAiMatchLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!messageDialog || !messageContent.trim()) return;
    setSendingMessage(true);
    try {
      await messagesAPI.send(messageDialog.user_id || messageDialog._id, messageContent);
      toast.success(`Message sent to ${messageDialog.first_name || messageDialog.name}`);
      setMessageDialog(null);
      setMessageContent('');
    } catch (e) {
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const fetchJobs = async () => {
    setJobsLoading(true);
    try {
      const res = await recruiterAPI.getJobs({ limit: 50 });
      setJobs(res.data?.jobs || res.data || []);
    } catch (e) {
      toast.error('Failed to load jobs');
    } finally {
      setJobsLoading(false);
    }
  };

  // Parse a pasted job posting via AI and pre-fill the form
  const handleParseJobPaste = async () => {
    const text = jobPasteText.trim();
    if (!text) {
      toast.error('Paste a job posting first');
      return;
    }
    setParsingJob(true);
    try {
      const res = await jobsAPI.parse(text);
      const p = res.data || {};
      // Map parser values to this form's allowed enum sets
      const validWorkModes = new Set(WORK_MODES.map(w => w.value));
      // Parser returns 'on_site'; this form uses 'onsite'. Normalize.
      const normalizedWorkMode = p.work_mode === 'on_site' ? 'onsite' : p.work_mode;
      const workMode = validWorkModes.has(normalizedWorkMode) ? normalizedWorkMode : newJob.work_mode;
      const validExpLevels = new Set(EXPERIENCE_LEVELS.map(e => e.value));
      const expLevel = validExpLevels.has(p.experience_level) ? p.experience_level : newJob.experience_level;
      const knownCategory = JOB_CATEGORIES.find(c => c.toLowerCase() === (p.category || '').toLowerCase());

      // RecruiterPanel form has no requirements field — append them onto the description so they're not lost
      let description = p.description || '';
      if (Array.isArray(p.requirements) && p.requirements.length) {
        const reqBlock = '\n\n**Requirements**\n' + p.requirements.map(r => `- ${r}`).join('\n');
        if (!/requirements/i.test(description)) description += reqBlock;
      }

      setNewJob(j => ({
        ...j,
        title: p.title || j.title,
        company: p.company || j.company,
        location: p.location || j.location,
        description: description || j.description,
        work_mode: workMode,
        experience_level: expLevel,
        salary_min: p.salary_min != null ? String(p.salary_min) : j.salary_min,
        salary_max: p.salary_max != null ? String(p.salary_max) : j.salary_max,
        category: knownCategory || j.category,
      }));
      toast.success('Form filled — review before posting');
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Failed to parse posting';
      toast.error(msg);
    } finally {
      setParsingJob(false);
    }
  };

  const handleCreateJob = async () => {
    if (!newJob.title.trim() || !newJob.company.trim()) {
      toast.error('Title and company are required');
      return;
    }
    setCreatingJob(true);
    try {
      const payload = {
        ...newJob,
        salary_min: newJob.salary_min ? Number(newJob.salary_min) : undefined,
        salary_max: newJob.salary_max ? Number(newJob.salary_max) : undefined,
      };
      await jobsAPI.create(payload);
      toast.success('Job posted successfully');
      setShowCreateJob(false);
      setNewJob({
        title: '', company: '', description: '', location: '',
        work_mode: 'remote', experience_level: 'mid',
        salary_min: '', salary_max: '', category: '',
      });
      setJobPasteText('');
      fetchJobs();
    } catch (e) {
      toast.error('Failed to create job');
    } finally {
      setCreatingJob(false);
    }
  };

  const fetchPipeline = async () => {
    setPipelineLoading(true);
    try {
      const res = await recruiterAPI.getPipeline({ limit: 200 });
      setPipeline(res.data?.applications || res.data || []);
    } catch (e) {
      toast.error('Failed to load pipeline');
    } finally {
      setPipelineLoading(false);
    }
  };

  const handleUpdatePipeline = async () => {
    if (!pipelineDetail) return;
    setUpdatingPipeline(true);
    try {
      await recruiterAPI.updatePipeline(pipelineDetail._id || pipelineDetail.application_id, {
        stage: pipelineStageUpdate || pipelineDetail.stage,
        notes: pipelineNotes,
      });
      toast.success('Pipeline updated');
      setPipelineDetail(null);
      fetchPipeline();
    } catch (e) {
      toast.error('Failed to update pipeline');
    } finally {
      setUpdatingPipeline(false);
    }
  };

  const handleAddToPipeline = async (candidate) => {
    setAddToPipelineDialog(candidate);
    setAddPipelineJobId('');
    setAddPipelineNotes('');
  };

  const submitAddToPipeline = async () => {
    if (!addToPipelineDialog) return;
    try {
      await recruiterAPI.addToPipeline({
        user_id: addToPipelineDialog.user_id || addToPipelineDialog._id,
        job_id: (addPipelineJobId && addPipelineJobId !== 'none') ? addPipelineJobId : undefined,
        notes: addPipelineNotes || undefined,
      });
      toast.success('Candidate added to pipeline');
      setAddToPipelineDialog(null);
    } catch (e) {
      toast.error('Failed to add to pipeline');
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'jobs') fetchJobs();
    if (tab === 'pipeline') fetchPipeline();
    if (tab === 'analytics') {
      fetchDashboard();
      fetchPipeline();
    }
  };

  const getInitials = (person) => {
    const first = person?.first_name || person?.name?.split(' ')[0] || '';
    const last = person?.last_name || person?.name?.split(' ').slice(1).join(' ') || '';
    return `${first[0] || ''}${last[0] || ''}`.toUpperCase() || '?';
  };

  const getDisplayName = (person) => {
    if (person?.first_name) return `${person.first_name} ${person.last_name || ''}`.trim();
    return person?.name || 'Unknown';
  };

  const getPipelineByStage = useCallback((stage) => {
    return pipeline.filter(app => (app.stage || 'new') === stage);
  }, [pipeline]);

  const getStageCounts = useCallback(() => {
    return PIPELINE_STAGES.map(s => ({
      name: s.label,
      count: pipeline.filter(app => (app.stage || 'new') === s.key).length,
      fill: s.key === 'new' ? '#3b82f6' : s.key === 'reviewed' ? '#6366f1' : s.key === 'contacted' ? '#a855f7'
        : s.key === 'interviewed' ? '#f59e0b' : s.key === 'offered' ? '#10b981' : '#ef4444',
    }));
  }, [pipeline]);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-dark-slate"></div>
        </div>
      </Layout>
    );
  }

  const stats = dashboardData || {};
  const activePostings = stats.active_postings ?? stats.activePostings ?? 0;
  const totalApplicants = stats.total_applicants ?? stats.totalApplicants ?? 0;
  const pipelineStats = stats.pipeline_stats ?? stats.pipelineStats ?? {};
  const recentApplications = stats.recent_applications ?? stats.recentApplications ?? [];
  const viewsData = stats.views_over_time ?? stats.viewsOverTime ?? [];
  const applicationsData = stats.applications_over_time ?? stats.applicationsOverTime ?? [];

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-1">
            <div className="p-2 bg-dark-slate rounded-lg">
              <Briefcase className="w-6 h-6 text-white" />
            </div>
            <h1 className="font-heading font-extrabold text-3xl text-dark-slate">Recruiter Panel</h1>
          </div>
          <p className="text-slate-gray ml-14">Discover talent, manage your pipeline, and post opportunities</p>
        </div>

        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList className="grid grid-cols-6 w-full max-w-3xl mb-8 bg-slate-100 rounded-xl p-1">
            <TabsTrigger value="dashboard" className="rounded-lg data-[state=active]:bg-white text-xs sm:text-sm">
              <BarChart3 className="w-4 h-4 mr-1.5" />Dashboard
            </TabsTrigger>
            <TabsTrigger value="talent" className="rounded-lg data-[state=active]:bg-white text-xs sm:text-sm">
              <Search className="w-4 h-4 mr-1.5" />Talent
            </TabsTrigger>
            <TabsTrigger value="jobs" className="rounded-lg data-[state=active]:bg-white text-xs sm:text-sm">
              <Briefcase className="w-4 h-4 mr-1.5" />Jobs
            </TabsTrigger>
            <TabsTrigger value="pipeline" className="rounded-lg data-[state=active]:bg-white text-xs sm:text-sm">
              <Target className="w-4 h-4 mr-1.5" />Pipeline
            </TabsTrigger>
            <TabsTrigger value="messaging" className="rounded-lg data-[state=active]:bg-white text-xs sm:text-sm">
              <MessageCircle className="w-4 h-4 mr-1.5" />Messages
            </TabsTrigger>
            <TabsTrigger value="analytics" className="rounded-lg data-[state=active]:bg-white text-xs sm:text-sm">
              <TrendingUp className="w-4 h-4 mr-1.5" />Analytics
            </TabsTrigger>
            <TabsTrigger value="settings" className="rounded-lg data-[state=active]:bg-white text-xs sm:text-sm">
              <Briefcase className="w-4 h-4 mr-1.5" />Settings
            </TabsTrigger>
          </TabsList>

          {/* ========== DASHBOARD TAB ========== */}
          <TabsContent value="dashboard">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <Card className="border-0 shadow-md">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-gray">Active Postings</p>
                      <p className="text-3xl font-bold text-dark-slate">{activePostings}</p>
                    </div>
                    <div className="p-3 bg-blue-50 rounded-xl">
                      <Briefcase className="w-6 h-6 text-blue-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-md">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-gray">Total Applicants</p>
                      <p className="text-3xl font-bold text-dark-slate">{totalApplicants}</p>
                    </div>
                    <div className="p-3 bg-emerald-50 rounded-xl">
                      <Users className="w-6 h-6 text-emerald-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-md">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-gray">Pipeline Stages</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {PIPELINE_STAGES.slice(0, 4).map(s => (
                          <Badge key={s.key} variant="outline" className={`${s.headerBg} ${s.textColor} text-xs`}>
                            {s.label}: {pipelineStats[s.key] ?? 0}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <div className="p-3 bg-purple-50 rounded-xl">
                      <Target className="w-6 h-6 text-purple-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card className="border-0 shadow-md">
              <CardHeader>
                <CardTitle className="text-lg text-dark-slate">Recent Applications</CardTitle>
              </CardHeader>
              <CardContent>
                {recentApplications.length === 0 ? (
                  <p className="text-slate-gray text-center py-8">No recent applications yet</p>
                ) : (
                  <div className="space-y-3">
                    {recentApplications.map((app, idx) => (
                      <div key={app._id || idx} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors">
                        <div className="flex items-center gap-3">
                          <Avatar className="w-10 h-10">
                            <AvatarImage src={app.applicant?.avatar || app.avatar} />
                            <AvatarFallback className="bg-dark-slate text-white text-sm">
                              {getInitials(app.applicant || app)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-semibold text-dark-slate">{getDisplayName(app.applicant || app)}</p>
                            <p className="text-sm text-slate-gray">{app.job_title || app.jobTitle || 'Position'}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                            {app.stage || 'new'}
                          </Badge>
                          {app.created_at && (
                            <span className="text-xs text-slate-gray">
                              {formatDistanceToNow(new Date(app.created_at), { addSuffix: true })}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ========== TALENT SEARCH TAB ========== */}
          <TabsContent value="talent">
            <Card className="border-0 shadow-md mb-6">
              <CardHeader>
                <CardTitle className="text-lg text-dark-slate flex items-center gap-2">
                  <Filter className="w-5 h-5" />Advanced Talent Search
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">Search</label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        placeholder="Name, skills, keywords..."
                        value={talentSearch}
                        onChange={(e) => setTalentSearch(e.target.value)}
                        className="pl-10"
                        onKeyDown={(e) => e.key === 'Enter' && searchTalent()}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">Institution</label>
                    <Select value={talentInstitution} onValueChange={setTalentInstitution}>
                      <SelectTrigger>
                        <SelectValue placeholder="All institutions" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Institutions</SelectItem>
                        {institutions.map(inst => (
                          <SelectItem key={inst._id || inst.name} value={inst.name || inst}>
                            {inst.name || inst}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">Major</label>
                    <Input
                      placeholder="e.g. Computer Science"
                      value={talentMajor}
                      onChange={(e) => setTalentMajor(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">Graduation Year</label>
                    <Select value={talentGradYear} onValueChange={setTalentGradYear}>
                      <SelectTrigger>
                        <SelectValue placeholder="Any year" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Any Year</SelectItem>
                        {GRADUATION_YEARS.map(y => (
                          <SelectItem key={y} value={y}>{y}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">Skills</label>
                    <Input
                      placeholder="e.g. Python, React, SQL"
                      value={talentSkills}
                      onChange={(e) => setTalentSkills(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">Location</label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        placeholder="City or state"
                        value={talentLocation}
                        onChange={(e) => setTalentLocation(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button onClick={searchTalent} disabled={talentLoading} className="bg-dark-slate hover:bg-dark-slate/90">
                    {talentLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                    Search Talent
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setTalentSearch('');
                      setTalentInstitution('all');
                      setTalentMajor('');
                      setTalentGradYear('all');
                      setTalentSkills('');
                      setTalentLocation('');
                      setTalentResults([]);
                    }}
                  >
                    <X className="w-4 h-4 mr-2" />Clear
                  </Button>
                </div>
              </CardContent>
            </Card>

            {talentLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            ) : talentResults.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {talentResults.map((candidate, idx) => (
                  <Card key={candidate._id || candidate.user_id || idx} className="border-0 shadow-md hover:shadow-lg transition-shadow">
                    <CardContent className="p-5">
                      <div className="flex items-start gap-3 mb-4">
                        <Avatar className="w-12 h-12">
                          <AvatarImage src={candidate.avatar || candidate.profile_picture} />
                          <AvatarFallback className="bg-dark-slate text-white">
                            {getInitials(candidate)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-dark-slate truncate">{getDisplayName(candidate)}</p>
                          <p className="text-sm text-slate-gray truncate">{candidate.title || candidate.headline || ''}</p>
                        </div>
                      </div>
                      <div className="space-y-1.5 mb-4">
                        {(candidate.institution || candidate.school) && (
                          <div className="flex items-center gap-2 text-sm text-slate-600">
                            <GraduationCap className="w-3.5 h-3.5 flex-shrink-0" />
                            <span className="truncate">{candidate.institution || candidate.school}</span>
                          </div>
                        )}
                        {candidate.major && (
                          <div className="flex items-center gap-2 text-sm text-slate-600">
                            <FileText className="w-3.5 h-3.5 flex-shrink-0" />
                            <span className="truncate">{candidate.major}</span>
                          </div>
                        )}
                        {candidate.location && (
                          <div className="flex items-center gap-2 text-sm text-slate-600">
                            <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                            <span className="truncate">{candidate.location}</span>
                          </div>
                        )}
                      </div>
                      {candidate.skills && candidate.skills.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-4">
                          {(Array.isArray(candidate.skills) ? candidate.skills : candidate.skills.split(',')).slice(0, 4).map((skill, si) => (
                            <Badge key={si} variant="outline" className="text-xs bg-slate-50">
                              {typeof skill === 'string' ? skill.trim() : skill}
                            </Badge>
                          ))}
                          {(Array.isArray(candidate.skills) ? candidate.skills.length : candidate.skills.split(',').length) > 4 && (
                            <Badge variant="outline" className="text-xs bg-slate-50">
                              +{(Array.isArray(candidate.skills) ? candidate.skills.length : candidate.skills.split(',').length) - 4}
                            </Badge>
                          )}
                        </div>
                      )}
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 text-xs"
                          onClick={() => {
                            setAiMatchDialog(candidate);
                            setAiJobDescription('');
                            setAiMatchResult(null);
                          }}
                        >
                          <Sparkles className="w-3.5 h-3.5 mr-1" />AI Match
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 text-xs"
                          onClick={() => handleAddToPipeline(candidate)}
                        >
                          <UserPlus className="w-3.5 h-3.5 mr-1" />Pipeline
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 text-xs"
                          onClick={() => {
                            setMessageDialog(candidate);
                            setMessageContent('');
                          }}
                        >
                          <Send className="w-3.5 h-3.5 mr-1" />Message
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-16 text-slate-gray">
                <Search className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="text-lg font-medium">Search for talent</p>
                <p className="text-sm">Use the filters above to find candidates from HBCU alumni networks</p>
              </div>
            )}
          </TabsContent>

          {/* ========== JOB POSTINGS TAB ========== */}
          <TabsContent value="jobs">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-dark-slate">Your Job Postings</h2>
              <Button onClick={() => setShowCreateJob(true)} className="bg-dark-slate hover:bg-dark-slate/90">
                <Plus className="w-4 h-4 mr-2" />Post New Job
              </Button>
            </div>

            {jobsLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            ) : jobs.length === 0 ? (
              <div className="text-center py-16 text-slate-gray">
                <Briefcase className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="text-lg font-medium">No job postings yet</p>
                <p className="text-sm mb-4">Create your first job posting to start attracting talent</p>
                <Button onClick={() => setShowCreateJob(true)} className="bg-dark-slate hover:bg-dark-slate/90">
                  <Plus className="w-4 h-4 mr-2" />Post New Job
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {jobs.map((job, idx) => (
                  <Card key={job._id || idx} className="border-0 shadow-md hover:shadow-lg transition-shadow">
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-semibold text-lg text-dark-slate">{job.title}</h3>
                            {job.status === 'active' && (
                              <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Active</Badge>
                            )}
                            {job.status === 'closed' && (
                              <Badge className="bg-slate-100 text-slate-600 border-slate-200">Closed</Badge>
                            )}
                          </div>
                          <div className="flex flex-wrap items-center gap-4 text-sm text-slate-gray mb-2">
                            {job.company && (
                              <span className="flex items-center gap-1"><Building2 className="w-3.5 h-3.5" />{job.company}</span>
                            )}
                            {job.location && (
                              <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{job.location}</span>
                            )}
                            {job.work_mode && (
                              <Badge variant="outline" className="text-xs">{job.work_mode}</Badge>
                            )}
                            {job.experience_level && (
                              <Badge variant="outline" className="text-xs">{job.experience_level}</Badge>
                            )}
                          </div>
                          {job.description && (
                            <p className="text-sm text-slate-600 line-clamp-2">{job.description}</p>
                          )}
                        </div>
                        <div className="text-right ml-4 flex-shrink-0">
                          <div className="flex items-center gap-1 text-slate-gray">
                            <Users className="w-4 h-4" />
                            <span className="font-semibold text-dark-slate">{job.application_count ?? job.applicationCount ?? 0}</span>
                          </div>
                          <p className="text-xs text-slate-gray mt-1">applicants</p>
                          {job.created_at && (
                            <p className="text-xs text-slate-gray mt-2">
                              {formatDistanceToNow(new Date(job.created_at), { addSuffix: true })}
                            </p>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* ========== PIPELINE TAB ========== */}
          <TabsContent value="pipeline">
            <div className="mb-4">
              <h2 className="text-xl font-bold text-dark-slate mb-1">Candidate Pipeline</h2>
              <p className="text-sm text-slate-gray">Track candidates through your hiring stages</p>
            </div>

            {pipelineLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            ) : (
              <div className="flex gap-4 overflow-x-auto pb-4" style={{ minHeight: '400px' }}>
                {PIPELINE_STAGES.map(stage => {
                  const stageApps = getPipelineByStage(stage.key);
                  return (
                    <div key={stage.key} className="flex-shrink-0 w-64">
                      <div className={`rounded-t-xl border-t-4 ${stage.color} px-3 py-2 ${stage.headerBg} border-x border-b-0`}>
                        <div className="flex items-center justify-between">
                          <h3 className={`font-semibold text-sm ${stage.textColor}`}>{stage.label}</h3>
                          <Badge variant="outline" className={`${stage.textColor} text-xs`}>
                            {stageApps.length}
                          </Badge>
                        </div>
                      </div>
                      <div className="bg-slate-50 border border-t-0 rounded-b-xl p-2 space-y-2 min-h-[320px]">
                        {stageApps.length === 0 ? (
                          <p className="text-xs text-slate-400 text-center py-6">No candidates</p>
                        ) : (
                          stageApps.map((app, idx) => (
                            <div
                              key={app._id || app.application_id || idx}
                              className="bg-white rounded-lg p-3 shadow-sm border border-slate-100 cursor-pointer hover:shadow-md transition-shadow"
                              onClick={() => {
                                setPipelineDetail(app);
                                setPipelineStageUpdate(app.stage || 'new');
                                setPipelineNotes(app.notes || '');
                              }}
                            >
                              <div className="flex items-center gap-2 mb-1.5">
                                <Avatar className="w-7 h-7">
                                  <AvatarImage src={app.candidate?.avatar || app.avatar} />
                                  <AvatarFallback className="bg-dark-slate text-white text-xs">
                                    {getInitials(app.candidate || app)}
                                  </AvatarFallback>
                                </Avatar>
                                <div className="min-w-0 flex-1">
                                  <p className="font-medium text-sm text-dark-slate truncate">
                                    {getDisplayName(app.candidate || app)}
                                  </p>
                                </div>
                              </div>
                              {(app.candidate?.title || app.title) && (
                                <p className="text-xs text-slate-600 truncate mb-1">
                                  {app.candidate?.title || app.title}
                                </p>
                              )}
                              {(app.candidate?.institution || app.institution) && (
                                <p className="text-xs text-slate-400 truncate mb-1.5">
                                  {app.candidate?.institution || app.institution}
                                </p>
                              )}
                              <div className="flex items-center justify-between">
                                {app.created_at && (
                                  <span className="text-[10px] text-slate-400">
                                    {formatDistanceToNow(new Date(app.created_at), { addSuffix: true })}
                                  </span>
                                )}
                                {app.notes && (
                                  <FileText className="w-3 h-3 text-slate-400" />
                                )}
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* ========== MESSAGING TAB ========== */}
          <TabsContent value="messaging">
            <Card className="border-0 shadow-md">
              <CardHeader>
                <CardTitle className="text-lg text-dark-slate flex items-center gap-2">
                  <MessageCircle className="w-5 h-5" />Pipeline Candidates
                </CardTitle>
              </CardHeader>
              <CardContent>
                {pipeline.length === 0 ? (
                  <div className="text-center py-12 text-slate-gray">
                    <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p className="text-lg font-medium">No candidates in pipeline</p>
                    <p className="text-sm">Add candidates from the Talent Search tab to message them</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {pipeline.map((app, idx) => {
                      const person = app.candidate || app;
                      return (
                        <div key={app._id || idx} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors">
                          <div className="flex items-center gap-3">
                            <Avatar className="w-10 h-10">
                              <AvatarImage src={person.avatar || person.profile_picture} />
                              <AvatarFallback className="bg-dark-slate text-white text-sm">
                                {getInitials(person)}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="font-semibold text-dark-slate">{getDisplayName(person)}</p>
                              <div className="flex items-center gap-2">
                                {(person.title || person.headline) && (
                                  <p className="text-sm text-slate-gray">{person.title || person.headline}</p>
                                )}
                                <Badge variant="outline" className="text-xs">
                                  {app.stage || 'new'}
                                </Badge>
                              </div>
                            </div>
                          </div>
                          <Button
                            size="sm"
                            onClick={() => {
                              setMessageDialog({ ...person, user_id: person.user_id || person._id || app.user_id });
                              setMessageContent('');
                            }}
                            className="bg-dark-slate hover:bg-dark-slate/90"
                          >
                            <Send className="w-4 h-4 mr-2" />Send Message
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ========== ANALYTICS TAB ========== */}
          <TabsContent value="analytics">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <Card className="border-0 shadow-md">
                <CardHeader>
                  <CardTitle className="text-lg text-dark-slate flex items-center gap-2">
                    <Eye className="w-5 h-5" />Job Views Over Time
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {viewsData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <LineChart data={viewsData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                        <YAxis tick={{ fontSize: 12 }} stroke="#94a3b8" />
                        <Tooltip
                          contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                        />
                        <Line type="monotone" dataKey="views" stroke="#3b82f6" strokeWidth={2} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-[250px] text-slate-gray">
                      <p className="text-sm">No view data available yet</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="border-0 shadow-md">
                <CardHeader>
                  <CardTitle className="text-lg text-dark-slate flex items-center gap-2">
                    <FileText className="w-5 h-5" />Applications Over Time
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {applicationsData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={applicationsData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                        <YAxis tick={{ fontSize: 12 }} stroke="#94a3b8" />
                        <Tooltip
                          contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                        />
                        <Bar dataKey="applications" fill="#6366f1" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-[250px] text-slate-gray">
                      <p className="text-sm">No application data available yet</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            <Card className="border-0 shadow-md">
              <CardHeader>
                <CardTitle className="text-lg text-dark-slate flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />Pipeline Conversion Funnel
                </CardTitle>
              </CardHeader>
              <CardContent>
                {pipeline.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={getStageCounts()} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} stroke="#94a3b8" width={90} />
                      <Tooltip
                        contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                      />
                      <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                        {getStageCounts().map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-[300px] text-slate-gray">
                    <div className="text-center">
                      <Target className="w-10 h-10 mx-auto mb-2 opacity-30" />
                      <p className="text-sm">Add candidates to your pipeline to see funnel analytics</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ========== SETTINGS TAB ========== */}
          <TabsContent value="settings">
            <Card className="border-0 shadow-md">
              <CardHeader>
                <CardTitle className="text-lg text-dark-slate flex items-center gap-2">
                  <Briefcase className="w-5 h-5" />Integrations
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="bg-white rounded-2xl p-6 border border-slate-100">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-dark-slate">Greenhouse</h3>
                      <p className="text-xs text-slate-gray">Push applications to your Greenhouse ATS</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">API Key</label>
                      <Input
                        type="password"
                        placeholder="Your Greenhouse Harvest API key"
                        id="gh-api-key"
                        defaultValue=""
                        className="rounded-xl"
                      />
                      <p className="text-[10px] text-slate-gray mt-1">Find this in Greenhouse → Configure → Dev Center → API Credentials</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <Button
                        onClick={async () => {
                          const key = document.getElementById('gh-api-key')?.value;
                          if (!key) { toast.error('Enter an API key'); return; }
                          try {
                            await recruiterAPI.updateSettings({ greenhouse_api_key: key, greenhouse_enabled: true });
                            toast.success('Greenhouse connected! Applications will auto-push.');
                          } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
                        }}
                        className="bg-emerald-600 text-white hover:bg-emerald-700 rounded-full"
                        size="sm"
                      >
                        Connect Greenhouse
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-full text-xs"
                        onClick={async () => {
                          try {
                            await recruiterAPI.updateSettings({ greenhouse_enabled: false });
                            toast.success('Greenhouse disconnected');
                          } catch (e) { toast.error('Failed to disconnect'); }
                        }}
                      >
                        Disconnect
                      </Button>
                    </div>
                  </div>
                </div>
                {/* Salesforce Integration */}
                <div className="bg-white rounded-2xl p-6 border border-slate-100">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                      <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" fill="#00A1E0"/></svg>
                    </div>
                    <div>
                      <h3 className="font-semibold text-dark-slate">Salesforce</h3>
                      <p className="text-xs text-slate-gray">Sync candidates and applications with Salesforce CRM</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">Instance URL</label>
                      <Input placeholder="https://yourorg.my.salesforce.com" id="sf-instance-url" defaultValue="" className="rounded-xl" />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">API Token</label>
                      <Input type="password" placeholder="Your Salesforce API token" id="sf-api-token" defaultValue="" className="rounded-xl" />
                      <p className="text-[10px] text-slate-gray mt-1">Find this in Salesforce → Settings → My Personal Information → Security Token</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <Button onClick={async () => {
                        const url = document.getElementById('sf-instance-url')?.value;
                        const token = document.getElementById('sf-api-token')?.value;
                        if (!url || !token) { toast.error('Enter instance URL and token'); return; }
                        try {
                          await recruiterAPI.updateSettings({ salesforce_instance_url: url, salesforce_api_token: token, salesforce_enabled: true });
                          toast.success('Salesforce connected!');
                        } catch (e) { toast.error(e.response?.data?.detail || 'Failed to connect'); }
                      }} className="bg-blue-600 text-white hover:bg-blue-700 rounded-full" size="sm">
                        Connect Salesforce
                      </Button>
                      <Button variant="outline" size="sm" className="rounded-full text-xs" onClick={async () => {
                        try { await recruiterAPI.updateSettings({ salesforce_enabled: false }); toast.success('Salesforce disconnected'); }
                        catch { toast.error('Failed to disconnect'); }
                      }}>Disconnect</Button>
                    </div>
                  </div>
                </div>

                {/* Workday Integration */}
                <div className="bg-white rounded-2xl p-6 border border-slate-100">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-orange-50 flex items-center justify-center">
                      <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" fill="#FF6200" opacity="0.15"/><circle cx="12" cy="8" r="3" fill="#FF6200"/><path d="M6 18c0-3.31 2.69-6 6-6s6 2.69 6 6" stroke="#FF6200" strokeWidth="2" fill="none"/></svg>
                    </div>
                    <div>
                      <h3 className="font-semibold text-dark-slate">Workday</h3>
                      <p className="text-xs text-slate-gray">Sync job postings and candidates with Workday Recruiting</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">Tenant URL</label>
                      <Input placeholder="https://wd5.myworkday.com/yourorg" id="wd-tenant-url" defaultValue="" className="rounded-xl" />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">API Token</label>
                      <Input type="password" placeholder="Your Workday API token" id="wd-api-token" defaultValue="" className="rounded-xl" />
                      <p className="text-[10px] text-slate-gray mt-1">Generate in Workday → Integration → API Client → Create OAuth 2.0 Client</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <Button onClick={async () => {
                        const url = document.getElementById('wd-tenant-url')?.value;
                        const token = document.getElementById('wd-api-token')?.value;
                        if (!url || !token) { toast.error('Enter tenant URL and token'); return; }
                        try {
                          await recruiterAPI.updateSettings({ workday_tenant_url: url, workday_api_token: token, workday_enabled: true });
                          toast.success('Workday connected!');
                        } catch (e) { toast.error(e.response?.data?.detail || 'Failed to connect'); }
                      }} className="bg-orange-600 text-white hover:bg-orange-700 rounded-full" size="sm">
                        Connect Workday
                      </Button>
                      <Button variant="outline" size="sm" className="rounded-full text-xs" onClick={async () => {
                        try { await recruiterAPI.updateSettings({ workday_enabled: false }); toast.success('Workday disconnected'); }
                        catch { toast.error('Failed to disconnect'); }
                      }}>Disconnect</Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* ========== AI MATCH DIALOG ========== */}
        <Dialog open={!!aiMatchDialog} onOpenChange={(open) => { if (!open) setAiMatchDialog(null); }}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-600" />
                AI Match - {aiMatchDialog ? getDisplayName(aiMatchDialog) : ''}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">
                  Paste your job description
                </label>
                <Textarea
                  placeholder="Enter the full job description to match against this candidate..."
                  value={aiJobDescription}
                  onChange={(e) => setAiJobDescription(e.target.value)}
                  rows={6}
                  className="resize-none"
                />
              </div>
              {aiMatchResult && (
                <div className="p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl border border-purple-100">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="text-3xl font-bold text-purple-700">
                      {aiMatchResult.score ?? aiMatchResult.match_score ?? 'N/A'}
                      {typeof (aiMatchResult.score ?? aiMatchResult.match_score) === 'number' && (
                        <span className="text-lg font-normal text-purple-500">%</span>
                      )}
                    </div>
                    <span className="text-sm font-medium text-purple-600">Match Score</span>
                  </div>
                  {(aiMatchResult.summary || aiMatchResult.explanation) && (
                    <p className="text-sm text-slate-700">{aiMatchResult.summary || aiMatchResult.explanation}</p>
                  )}
                  {aiMatchResult.strengths && aiMatchResult.strengths.length > 0 && (
                    <div className="mt-2">
                      <p className="text-xs font-medium text-slate-600 mb-1">Strengths:</p>
                      <div className="flex flex-wrap gap-1">
                        {aiMatchResult.strengths.map((s, i) => (
                          <Badge key={i} className="bg-emerald-100 text-emerald-700 text-xs">{s}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  {aiMatchResult.gaps && aiMatchResult.gaps.length > 0 && (
                    <div className="mt-2">
                      <p className="text-xs font-medium text-slate-600 mb-1">Gaps:</p>
                      <div className="flex flex-wrap gap-1">
                        {aiMatchResult.gaps.map((g, i) => (
                          <Badge key={i} variant="outline" className="bg-amber-50 text-amber-700 text-xs">{g}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAiMatchDialog(null)}>Close</Button>
              <Button
                onClick={handleAiMatch}
                disabled={aiMatchLoading || !aiJobDescription.trim()}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {aiMatchLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                Run AI Match
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ========== MESSAGE DIALOG ========== */}
        <Dialog open={!!messageDialog} onOpenChange={(open) => { if (!open) setMessageDialog(null); }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Send className="w-5 h-5 text-blue-600" />
                Message {messageDialog ? getDisplayName(messageDialog) : ''}
              </DialogTitle>
            </DialogHeader>
            <div>
              <Textarea
                placeholder="Write your message..."
                value={messageContent}
                onChange={(e) => setMessageContent(e.target.value)}
                rows={5}
                className="resize-none"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setMessageDialog(null)}>Cancel</Button>
              <Button
                onClick={handleSendMessage}
                disabled={sendingMessage || !messageContent.trim()}
                className="bg-dark-slate hover:bg-dark-slate/90"
              >
                {sendingMessage ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Send
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ========== CREATE JOB DIALOG ========== */}
        <Dialog open={showCreateJob} onOpenChange={setShowCreateJob}>
          <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Plus className="w-5 h-5" />Post New Job
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {/* Smart Paste — AI extracts fields from a raw job posting */}
              <div className="rounded-lg border border-slate-200 bg-gradient-to-br from-indigo-50/40 to-purple-50/30 p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Sparkles className="w-4 h-4 text-slate-700" />
                  <span className="text-sm font-semibold text-slate-700">Paste a job posting (fills the form with AI)</span>
                </div>
                <p className="text-[11px] text-slate-500 mb-2 leading-relaxed">
                  Paste raw text from an email, LinkedIn message, or job board — AI extracts the title, company, salary, description, and more. Review and edit before posting.
                </p>
                <Textarea
                  value={jobPasteText}
                  onChange={(e) => setJobPasteText(e.target.value)}
                  placeholder="Paste the full job posting text here..."
                  rows={4}
                  className="text-sm bg-white"
                  disabled={parsingJob}
                />
                <div className="flex items-center gap-2 mt-2">
                  <Button
                    type="button"
                    onClick={handleParseJobPaste}
                    disabled={parsingJob || !jobPasteText.trim()}
                    size="sm"
                    className="bg-dark-slate hover:bg-dark-slate/90 text-white"
                  >
                    {parsingJob ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Parsing...</> : <><Sparkles className="w-3.5 h-3.5 mr-1.5" />Parse with AI</>}
                  </Button>
                  {jobPasteText && !parsingJob && (
                    <Button type="button" variant="ghost" size="sm" onClick={() => setJobPasteText('')} className="text-slate-500">
                      Clear
                    </Button>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Job Title *</label>
                  <Input
                    placeholder="e.g. Software Engineer"
                    value={newJob.title}
                    onChange={(e) => setNewJob(j => ({ ...j, title: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Company *</label>
                  <Input
                    placeholder="Company name"
                    value={newJob.company}
                    onChange={(e) => setNewJob(j => ({ ...j, company: e.target.value }))}
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Description</label>
                <Textarea
                  placeholder="Describe the role, responsibilities, and requirements..."
                  value={newJob.description}
                  onChange={(e) => setNewJob(j => ({ ...j, description: e.target.value }))}
                  rows={4}
                  className="resize-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Location</label>
                  <Input
                    placeholder="e.g. Atlanta, GA"
                    value={newJob.location}
                    onChange={(e) => setNewJob(j => ({ ...j, location: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Work Mode</label>
                  <Select value={newJob.work_mode} onValueChange={(v) => setNewJob(j => ({ ...j, work_mode: v }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {WORK_MODES.map(m => (
                        <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Experience Level</label>
                  <Select value={newJob.experience_level} onValueChange={(v) => setNewJob(j => ({ ...j, experience_level: v }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {EXPERIENCE_LEVELS.map(l => (
                        <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Category</label>
                  <Select value={newJob.category} onValueChange={(v) => setNewJob(j => ({ ...j, category: v }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {JOB_CATEGORIES.map(c => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Salary Min ($)</label>
                  <Input
                    type="number"
                    placeholder="e.g. 80000"
                    value={newJob.salary_min}
                    onChange={(e) => setNewJob(j => ({ ...j, salary_min: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Salary Max ($)</label>
                  <Input
                    type="number"
                    placeholder="e.g. 120000"
                    value={newJob.salary_max}
                    onChange={(e) => setNewJob(j => ({ ...j, salary_max: e.target.value }))}
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateJob(false)}>Cancel</Button>
              <Button
                onClick={handleCreateJob}
                disabled={creatingJob}
                className="bg-dark-slate hover:bg-dark-slate/90"
              >
                {creatingJob ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                Post Job
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ========== PIPELINE DETAIL DIALOG ========== */}
        <Dialog open={!!pipelineDetail} onOpenChange={(open) => { if (!open) setPipelineDetail(null); }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Candidate Details</DialogTitle>
            </DialogHeader>
            {pipelineDetail && (
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Avatar className="w-12 h-12">
                    <AvatarImage src={pipelineDetail.candidate?.avatar || pipelineDetail.avatar} />
                    <AvatarFallback className="bg-dark-slate text-white">
                      {getInitials(pipelineDetail.candidate || pipelineDetail)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-semibold text-dark-slate">
                      {getDisplayName(pipelineDetail.candidate || pipelineDetail)}
                    </p>
                    {(pipelineDetail.candidate?.title || pipelineDetail.title) && (
                      <p className="text-sm text-slate-gray">
                        {pipelineDetail.candidate?.title || pipelineDetail.title}
                      </p>
                    )}
                    {(pipelineDetail.candidate?.institution || pipelineDetail.institution) && (
                      <p className="text-sm text-slate-gray">
                        {pipelineDetail.candidate?.institution || pipelineDetail.institution}
                      </p>
                    )}
                  </div>
                </div>
                {pipelineDetail.created_at && (
                  <p className="text-xs text-slate-gray">
                    Added {formatDistanceToNow(new Date(pipelineDetail.created_at), { addSuffix: true })}
                    {pipelineDetail.source === 'user_applied' && <Badge className="ml-2 text-[10px] bg-blue-100 text-blue-700">Applied</Badge>}
                    {pipelineDetail.source === 'recruiter_added' && <Badge className="ml-2 text-[10px] bg-slate-100 text-slate-600">Added by you</Badge>}
                  </p>
                )}
                {pipelineDetail.cover_letter && (
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">Cover Letter</label>
                    <p className="text-sm text-slate-gray bg-slate-50 rounded-lg p-3 whitespace-pre-wrap max-h-32 overflow-y-auto">{pipelineDetail.cover_letter}</p>
                  </div>
                )}
                {pipelineDetail.resume_url && (
                  <div>
                    <a href={pipelineDetail.resume_url} target="_blank" rel="noopener noreferrer">
                      <Button variant="outline" size="sm" className="rounded-full text-xs">
                        <FileText className="w-3.5 h-3.5 mr-1" />View Resume
                      </Button>
                    </a>
                  </div>
                )}
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Stage</label>
                  <Select value={pipelineStageUpdate} onValueChange={setPipelineStageUpdate}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PIPELINE_STAGES.map(s => (
                        <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Notes</label>
                  <Textarea
                    placeholder="Add notes about this candidate..."
                    value={pipelineNotes}
                    onChange={(e) => setPipelineNotes(e.target.value)}
                    rows={4}
                    className="resize-none"
                  />
                </div>
              </div>
            )}
            <DialogFooter className="flex-wrap gap-2">
              <Button variant="outline" onClick={() => setPipelineDetail(null)}>Cancel</Button>
              <Button
                variant="outline"
                size="sm"
                className="text-xs"
                onClick={async () => {
                  try {
                    toast.loading('Pushing to Greenhouse...', { id: 'gh' });
                    await recruiterAPI.pushToGreenhouse(pipelineDetail._id || pipelineDetail.application_id);
                    toast.success('Pushed to Greenhouse!', { id: 'gh' });
                  } catch (e) {
                    toast.error(e.response?.data?.detail || 'Push failed', { id: 'gh' });
                  }
                }}
              >
                Push to Greenhouse
              </Button>
              <Button
                onClick={handleUpdatePipeline}
                disabled={updatingPipeline}
                className="bg-dark-slate hover:bg-dark-slate/90"
              >
                {updatingPipeline ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ChevronRight className="w-4 h-4 mr-2" />}
                Update
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ========== ADD TO PIPELINE DIALOG ========== */}
        <Dialog open={!!addToPipelineDialog} onOpenChange={(open) => { if (!open) setAddToPipelineDialog(null); }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserPlus className="w-5 h-5" />
                Add to Pipeline
              </DialogTitle>
            </DialogHeader>
            {addToPipelineDialog && (
              <div className="space-y-4">
                <p className="text-sm text-slate-600">
                  Add <span className="font-semibold text-dark-slate">{getDisplayName(addToPipelineDialog)}</span> to your recruiting pipeline.
                </p>
                {jobs.length > 0 && (
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">For Job (optional)</label>
                    <Select value={addPipelineJobId} onValueChange={setAddPipelineJobId}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a job posting" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No specific job</SelectItem>
                        {jobs.map(j => (
                          <SelectItem key={j._id} value={j._id}>{j.title} - {j.company}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Notes (optional)</label>
                  <Textarea
                    placeholder="Why are you adding this candidate?"
                    value={addPipelineNotes}
                    onChange={(e) => setAddPipelineNotes(e.target.value)}
                    rows={3}
                    className="resize-none"
                  />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setAddToPipelineDialog(null)}>Cancel</Button>
              <Button
                onClick={submitAddToPipeline}
                className="bg-dark-slate hover:bg-dark-slate/90"
              >
                <UserPlus className="w-4 h-4 mr-2" />Add to Pipeline
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default RecruiterPanel;
